import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { StatsCard } from "@/components/stats-card";
import {
  FileText, CheckCircle2, Loader2, AlertTriangle, Coins,
  BarChart3, RefreshCw, Eye, RotateCcw, Play, Database,
  ChevronsUpDown, ChevronUp, ChevronDown,
} from "lucide-react";
import { toast } from "sonner";
import { serpAnalysisApi, type AnalysisListItem } from "@/api/serp-analysis";

// ── Sort helpers ──

declare const __BUILD_TS__: number
const _DEDUP_BUILD = __BUILD_TS__

const STATUS_ORDER: Record<string, number> = {
  stuck: 0,
  failed: 1,
  running: 2,
  pending: 3,
  completed: 4,
};

// Deduplicate by keyword: keep the entry with most data.
// Score = steps_completed * 1000 + parsed_htags; ties broken by newest created_at.
function deduplicateByKeyword(list: AnalysisListItem[]): AnalysisListItem[] {
  const best = new Map<string, AnalysisListItem>();
  for (const item of list) {
    const key = item.keyword.toLowerCase().trim();
    const prev = best.get(key);
    if (!prev) { best.set(key, item); continue; }
    const score = (it: AnalysisListItem) => it.steps_completed * 1000 + it.parsed_htags;
    if (score(item) > score(prev) ||
        (score(item) === score(prev) && item.created_at > prev.created_at)) {
      best.set(key, item);
    }
  }
  return Array.from(best.values());
}

type SortDir = "asc" | "desc" | null;

function nextDir(cur: SortDir): SortDir {
  if (cur === null) return "asc";
  if (cur === "asc") return "desc";
  return null;
}

function SortIcon({ dir }: { dir: SortDir }) {
  if (dir === "asc") return <ChevronUp className="h-3 w-3 ml-1 inline" />;
  if (dir === "desc") return <ChevronDown className="h-3 w-3 ml-1 inline" />;
  return <ChevronsUpDown className="h-3 w-3 ml-1 inline opacity-40" />;
}

// ── Status helpers ──

function statusBadge(status: string) {
  switch (status) {
    case "completed":
      return <Badge variant="default" className="bg-green-600">✅ 完成</Badge>;
    case "running":
      return <Badge variant="secondary" className="bg-blue-100 text-blue-800">🔄 分析中</Badge>;
    case "failed":
      return <Badge variant="destructive">❌ 失敗</Badge>;
    case "pending":
      return <Badge variant="outline">⏳ 待處理</Badge>;
    case "stuck":
      return <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">⚠️ 卡住</Badge>;
    default:
      return <Badge variant="outline">{status}</Badge>;
  }
}

function stepsDisplay(completed: number, total: number) {
  return (
    <span className="text-sm text-muted-foreground">
      {completed}/{total}
    </span>
  );
}

function formatEstimatedUsd(cost: number) {
  return `US$${cost.toLocaleString(undefined, { minimumFractionDigits: 4, maximumFractionDigits: 4 })}`;
}

function costDisplay(cost: number, legacyCredits: number) {
  if (cost <= 0 && legacyCredits <= 0) return "—";
  return (
    <div className="space-y-0.5">
      {cost > 0 && <div>{formatEstimatedUsd(cost)} <span className="text-xs text-muted-foreground">估算</span></div>}
      {legacyCredits > 0 && <div className="text-xs text-muted-foreground">{legacyCredits.toFixed(4)} Credits 舊制</div>}
    </div>
  );
}

// ── Page component ──

export default function AnalysisReports() {
  const navigate = useNavigate();
  const [retrying, setRetrying] = useState<string | null>(null);
  const [triggering, setTriggering] = useState<string | null>(null);
  const [fetching, setFetching] = useState<string | null>(null);
  const [batchAnalyzing, setBatchAnalyzing] = useState(false);
  const [batchFetching, setBatchFetching] = useState(false);
  const [batchRetrying, setBatchRetrying] = useState(false);
  const [forceRecrawling, setForceRecrawling] = useState<string | null>(null);
  const [batchForceRecrawling, setBatchForceRecrawling] = useState(false);
  const [statusSort, setStatusSort] = useState<SortDir>(null);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["analysis-reports"],
    queryFn: async () => {
      const res = await serpAnalysisApi.list({ limit: 100 });
      return res.data;
    },
    refetchInterval: 15_000, // Auto-refresh for running analyses
  });

  const stats = data?.stats;
  const rawItems = data?.items ?? [];
  const dedupedItems = deduplicateByKeyword(rawItems);
  const items = statusSort === null
    ? dedupedItems
    : [...dedupedItems].sort((a, b) => {
        const diff = (STATUS_ORDER[a.status] ?? 99) - (STATUS_ORDER[b.status] ?? 99);
        return statusSort === "asc" ? diff : -diff;
      });

  const forceRecrawlOne = async (requestId: string, keyword: string) => {
    setForceRecrawling(requestId);
    try {
      const res = await serpAnalysisApi.forceRecrawl(requestId);
      const d = res.data.data;
      toast.success(
        `已啟動強制重爬：${d.keyword}，清除 ${d.failed_tasks_deleted} 筆失敗任務，略過 ${d.blocked_skipped} 個大電商，正在重新排程爬取`
      );
      refetch();
    } catch {
      toast.error(`強制重爬失敗：${keyword}`);
    } finally {
      setForceRecrawling(null);
    }
  };

  if (error) {
    return (
      <div className="p-6 space-y-4">
        <h1 className="text-2xl font-bold">SERP Analysis Reports — SERP 分析報告</h1>
        <Card>
          <CardContent className="py-12 text-center">
            <AlertTriangle className="h-12 w-12 mx-auto text-yellow-500 mb-4" />
            <p className="text-lg font-medium">無法載入分析報告</p>
            <p className="text-sm text-muted-foreground mt-2">
              {(error as Error).message}
            </p>
            <Button onClick={() => refetch()} variant="outline" className="mt-4">
              <RefreshCw className="h-4 w-4 mr-2" />
              重試
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">SERP Analysis Reports — SERP 分析報告</h1>
        <Button onClick={() => refetch()} variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-2" />
          重新整理
        </Button>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <StatsCard
          title="總分析"
          value={stats?.total ?? 0}
          icon={BarChart3}
          color="primary"
        />
        <StatsCard
          title="完成"
          value={stats?.completed ?? 0}
          icon={CheckCircle2}
          color="success"
        />
        <StatsCard
          title="進行中"
          value={stats?.running ?? 0}
          icon={Loader2}
          color="info"
        />
        <StatsCard
          title="失敗"
          value={stats?.failed ?? 0}
          icon={AlertTriangle}
          color="destructive"
        />
        <StatsCard
          title="LLM 估算費用"
          value={stats ? formatEstimatedUsd(stats.total_cost) : "US$0.0000"}
          description={stats && ((stats as any).legacy_cost_credits ?? 0) > 0
            ? `另含舊制 ${Number((stats as any).legacy_cost_credits).toFixed(4)} Credits`
            : "Personal + Company，非供應商正式帳單"}
          icon={Coins}
          color="warning"
        />
        <StatsCard
          title="平均估算費用"
          value={
            stats && stats.completed > 0
              ? formatEstimatedUsd(stats.total_cost / stats.completed)
              : "US$0.0000"
          }
          description="每次分析"
          icon={Coins}
          color="info"
        />
      </div>

      {/* Report list table */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
          <CardTitle className="text-lg">分析紀錄</CardTitle>
          <div className="flex gap-2 flex-wrap">
            {/* Batch retry: all failed items */}
            {items.filter((it: AnalysisListItem) => it.status === "failed").length > 0 && (
              <Button
                size="sm"
                variant="outline"
                className="text-red-600 border-red-300 hover:bg-red-50"
                disabled={batchRetrying}
                title="重新觸發所有失敗的分析任務"
                onClick={async () => {
                  const targets = items.filter((it: AnalysisListItem) => it.status === "failed");
                  setBatchRetrying(true);
                  let ok = 0; let fail = 0;
                  for (const it of targets) {
                    try {
                      await serpAnalysisApi.trigger(it.request_id, it.keyword);
                      ok++;
                      await new Promise(r => setTimeout(r, 500));
                    } catch { fail++; }
                  }
                  setBatchRetrying(false);
                  toast.success(`批量重試完成 — ${ok} 個已重新觸發${fail > 0 ? `，${fail} 個失敗` : ""}`);
                  refetch();
                }}
              >
                {batchRetrying ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <RotateCcw className="h-3 w-3 mr-1" />}
                批量重試
                <span className="ml-1 bg-red-500 text-white text-xs rounded-full px-1.5 py-0.5">
                  {items.filter((it: AnalysisListItem) => it.status === "failed").length}
                </span>
              </Button>
            )}
            {/* Batch fetch competitors: all items where analysis_ready=false and no LLM started */}
            <Button
              size="sm"
              variant="outline"
              className="text-orange-600 border-orange-300 hover:bg-orange-50"
              disabled={batchFetching}
              title="對所有尚未就緒的關鍵字批次補爬競品資料"
              onClick={async () => {
                const targets = items.filter(
                  (it: AnalysisListItem) => !it.analysis_ready && it.status === "pending" && it.steps_total === 0
                );
                if (targets.length === 0) {
                  toast.info("沒有需要補資料的關鍵字（全部已就緒或已開始分析）");
                  return;
                }
                setBatchFetching(true);
                toast.info(`正在對 ${targets.length} 個關鍵字發送補資料請求…`);
                let queued = 0; let serpRefreshed = 0; let skipped = 0;
                // Fire all fetchCompetitors in parallel (fast)
                const results = await Promise.allSettled(
                  targets.map(it => serpAnalysisApi.fetchCompetitors(it.request_id))
                );
                // For items with no_serp_data, fire refreshSerpResults in parallel (slow, don't await result)
                const serpNeeded: AnalysisListItem[] = [];
                results.forEach((r, i) => {
                  if (r.status === "fulfilled") {
                    const d = r.value.data as { queued?: number; no_serp_data?: boolean } | undefined;
                    if (d?.no_serp_data) serpNeeded.push(targets[i]);
                    else if ((d?.queued ?? 0) > 0) queued += d!.queued!;
                    else skipped++;
                  } else skipped++;
                });
                if (serpNeeded.length > 0) {
                  // Fire all SERP refreshes in parallel — don't block UI
                  serpNeeded.forEach(it => {
                    serpAnalysisApi.refreshSerpResults(it.request_id).catch(() => {});
                  });
                  serpRefreshed = serpNeeded.length;
                }
                setBatchFetching(false);
                const parts = [];
                if (queued > 0) parts.push(`派出 ${queued} 個爬蟲任務`);
                if (serpRefreshed > 0) parts.push(`${serpRefreshed} 個已送出 SERP 抓取（背景執行，完成後再點一次補資料）`);
                if (skipped > 0) parts.push(`${skipped} 個跳過`);
                toast.success(`批次補資料：${parts.join('，') || '無變動'}`);
                refetch();
              }}
            >
              {batchFetching
                ? <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                : <Database className="h-3 w-3 mr-1" />}
              批量補資料
            </Button>

            {/* Batch start analysis: all items where analysis_ready=true and status=pending */}
            <Button
              size="sm"
              className="bg-blue-600 hover:bg-blue-700 text-white"
              disabled={batchAnalyzing}
              title="對所有就緒且尚未開始分析的關鍵字批次觸發 LLM 分析"
              onClick={async () => {
                const targets = items.filter(
                  (it: AnalysisListItem) => it.analysis_ready && it.status === "pending" && it.steps_total === 0
                );
                if (targets.length === 0) {
                  toast.info("沒有可批次分析的關鍵字（需要競品資料已就緒，且尚未開始分析）");
                  return;
                }
                setBatchAnalyzing(true);
                let started = 0;
                let failed = 0;
                for (const it of targets) {
                  try {
                    await serpAnalysisApi.trigger(it.request_id, it.keyword);
                    started++;
                    // Small delay to avoid hammering the API
                    await new Promise(r => setTimeout(r, 300));
                  } catch { failed++; }
                }
                setBatchAnalyzing(false);
                toast.success(`批量分析已觸發 — ${started} 個已啟動${failed > 0 ? `，${failed} 個失敗` : ""}`);
                refetch();
              }}
            >
              {batchAnalyzing
                ? <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                : <Play className="h-3 w-3 mr-1" />}
              批量開始分析
              {!batchAnalyzing && items.filter((it: AnalysisListItem) => it.analysis_ready && it.status === "pending" && it.steps_total === 0).length > 0 && (
                <span className="ml-1 bg-blue-500 text-white text-xs rounded-full px-1.5 py-0.5">
                  {items.filter((it: AnalysisListItem) => it.analysis_ready && it.status === "pending" && it.steps_total === 0).length}
                </span>
              )}
            </Button>

            {/* Batch force recrawl: all pending items without analysis_ready */}
            {items.some((i: AnalysisListItem) => i.status === 'pending' && !i.analysis_ready) && (
              <Button
                variant="outline"
                size="sm"
                disabled={batchForceRecrawling}
                onClick={async () => {
                  setBatchForceRecrawling(true);
                  const targets = items.filter((i: AnalysisListItem) => i.status === 'pending' && !i.analysis_ready);
                  let count = 0;
                  for (const item of targets) {
                    await forceRecrawlOne(item.request_id, item.keyword);
                    count++;
                    if (count < targets.length) {
                      await new Promise(r => setTimeout(r, 500));
                    }
                  }
                  setBatchForceRecrawling(false);
                  toast.success(`批量重爬完成：${count} 個關鍵字已重新排程`);
                }}
                className="text-orange-700 border-orange-400 hover:bg-orange-50"
              >
                {batchForceRecrawling ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}
                批量強制重爬
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              <span className="ml-3 text-muted-foreground">載入中…</span>
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-lg font-medium">尚無分析報告</p>
              <p className="text-sm text-muted-foreground mt-2">
                從 SERP 資料頁面選擇關鍵字觸發分析
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>關鍵字 Keyword</TableHead>
                  <TableHead
                    className="cursor-pointer select-none whitespace-nowrap"
                    onClick={() => setStatusSort(nextDir(statusSort))}
                    title="點擊排序狀態"
                  >
                    狀態 Status<SortIcon dir={statusSort} />
                  </TableHead>
                  <TableHead className="text-center">競品資料 Data</TableHead>
                  <TableHead className="text-center">步驟 Steps</TableHead>
                  <TableHead className="text-right">估算費用 Cost</TableHead>
                  <TableHead>建立時間 Created</TableHead>
                  <TableHead className="text-center">操作 Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item: AnalysisListItem) => (
                  <TableRow key={item.request_id} className="cursor-pointer hover:bg-muted/50">
                    <TableCell
                      className="font-medium"
                      onClick={() => navigate(`/analysis-reports/${item.request_id}`)}
                    >
                      {item.keyword}
                    </TableCell>
                    <TableCell>{statusBadge(item.status)}</TableCell>
                    <TableCell className="text-center">
                      <span className={`text-xs font-mono ${item.analysis_ready ? 'text-green-600' : 'text-orange-500'}`}>
                        {item.analysis_ready ? '✅' : '⚠️'} 已解析: {item.parsed_htags}/10
                      </span>
                    </TableCell>
                    <TableCell className="text-center">
                      {stepsDisplay(item.steps_completed, item.steps_total)}
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm">
                      {costDisplay(item.total_cost, Number((item as any).legacy_cost_credits ?? 0))}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {new Date(item.created_at).toLocaleString("zh-TW")}
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex gap-1 justify-center">
                        {(item.status === "pending" && item.steps_total === 0) ? (
                          item.analysis_ready ? (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-blue-600 border-blue-300 hover:bg-blue-50"
                              disabled={triggering === item.request_id}
                              onClick={async () => {
                                setTriggering(item.request_id);
                                try {
                                  const res = await serpAnalysisApi.trigger(item.request_id, item.keyword);
                                  const s = res?.data?.status;
                                  if (s === 'already_completed') {
                                    toast.info(`${item.keyword} 所有步驟已完成，無需觸發`);
                                  } else {
                                    toast.success(`已觸發分析 — ${item.keyword}`);
                                  }
                                  refetch();
                                } catch (e) {
                                  toast.error(`觸發分析失敗：${(e as Error).message}`);
                                } finally {
                                  setTriggering(null);
                                }
                              }}
                            >
                              {triggering === item.request_id ? (
                                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                              ) : (
                                <BarChart3 className="h-3 w-3 mr-1" />
                              )}
                              開始分析
                            </Button>
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={forceRecrawling === item.request_id}
                              onClick={() => forceRecrawlOne(item.request_id, item.keyword)}
                              className="text-orange-700 border-orange-400 hover:bg-orange-50"
                            >
                              {forceRecrawling === item.request_id
                                ? <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                : <RefreshCw className="h-3 w-3 mr-1" />}
                              強制重爬
                            </Button>
                          )
                        ) : item.status === "stuck" ? (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-yellow-600 border-yellow-300 hover:bg-yellow-50"
                            disabled={retrying === item.request_id}
                            title="分析卡住，重新觸發未完成的步驟"
                            onClick={async () => {
                              setRetrying(item.request_id);
                              try {
                                const res = await serpAnalysisApi.trigger(item.request_id, item.keyword);
                                const s = res?.data?.status;
                                if (s === "retrying") {
                                  toast.success(`重新觸發成功 — ${item.keyword}，分析已重新啟動`);
                                } else if (s === "already_completed") {
                                  toast.info(`${item.keyword} 所有步驟已完成，無需重新觸發`);
                                } else if (s === "already_started") {
                                  toast.info(`${item.keyword} 分析進行中，請稍後`);
                                } else {
                                  toast.success(`已送出重新觸發請求 — ${item.keyword}`);
                                }
                                refetch();
                              } catch (e) {
                                toast.error(`重新觸發失敗：${(e as Error).message}`);
                              } finally {
                                setRetrying(null);
                              }
                            }}
                          >
                            {retrying === item.request_id ? (
                              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                            ) : (
                              <RotateCcw className="h-3 w-3 mr-1" />
                            )}
                            重新觸發
                          </Button>
                        ) : item.status === "failed" ? (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={retrying === item.request_id}
                            onClick={async () => {
                              setRetrying(item.request_id);
                              try {
                                const res = await serpAnalysisApi.trigger(item.request_id, item.keyword);
                                const s = res?.data?.status;
                                if (s === "retrying") {
                                  toast.success(`重試成功 — ${item.keyword}，失敗步驟已重新啟動`);
                                } else if (s === "already_completed") {
                                  toast.info(`${item.keyword} 所有步驟已完成，無需重試`);
                                } else if (s === "already_started") {
                                  toast.info(`${item.keyword} 分析進行中，請稍後`);
                                } else {
                                  toast.success(`已送出重試請求 — ${item.keyword}`);
                                }
                                refetch();
                              } catch (e) {
                                toast.error(`重試失敗：${(e as Error).message}`);
                              } finally {
                                setRetrying(null);
                              }
                            }}
                          >
                            {retrying === item.request_id ? (
                              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                            ) : (
                              <RotateCcw className="h-3 w-3 mr-1" />
                            )}
                            重試
                          </Button>
                        ) : null}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => navigate(`/analysis-reports/${item.request_id}`)}
                        >
                          <Eye className="h-3 w-3 mr-1" />
                          查看
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
